import i18n from 'i18next'
import type { UserRole } from '@shared/config/constants'
import { LOCALE_TO_BACKEND_HEADER, LOCALES, toAppLocale } from '@shared/lib/locale'

export interface StoredUser {
  profile: {
    role: UserRole
    full_name_ru?: string
    full_name_en?: string
    image?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export const authTokens = {
  getAccess: () => localStorage.getItem('access_token'),
  getRefresh: () => localStorage.getItem('refresh_token'),
  setAccess: (token: string) => localStorage.setItem('access_token', token),
  setRefresh: (token: string) => localStorage.setItem('refresh_token', token),
  getRole: (): UserRole | null => localStorage.getItem('role') as UserRole | null,
  setRole: (role: UserRole) => localStorage.setItem('role', role),
  getUser: (): StoredUser | null => {
    const s = localStorage.getItem('user_data')
    return s ? (JSON.parse(s) as StoredUser) : null
  },
  setUser: (user: StoredUser) => localStorage.setItem('user_data', JSON.stringify(user)),
  clear: () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('role')
    localStorage.removeItem('user_data')
    localStorage.removeItem('backend_lang')
  },
}

/**
 * Свой profile id из сохранённой сессии.
 *
 * Отдельная функция, а не только хук: `useMyProfileId` мемоизирует значение на
 * монтировании, и сразу после входа оно ещё пустое — форме логина нужен
 * свежий id, чтобы решить, куда вести участника.
 */
export function getStoredProfileId(): number | null {
  const profile = authTokens.getUser()?.profile as { id?: number } | undefined
  return profile?.id ?? null
}

/** Maps the current UI language to the backend language code sent on every API
 *  request (Accept-Language / Content-Language header + `?language=` query).
 *  kz → 'kk', en → 'en', ru → 'ru'.
 *
 *  Prefers the LIVE i18n language so switching the UI language is reflected
 *  immediately — `backend_lang`/`lang`/`i18nextLng` from localStorage are only a
 *  fallback for the brief window before i18next has initialized.
 */
export function getBackendLang(): string {
  if (i18n.language) return LOCALE_TO_BACKEND_HEADER[toAppLocale(i18n.language)]
  const stored =
    localStorage.getItem('backend_lang') ??
    localStorage.getItem('lang') ??
    localStorage.getItem('i18nextLng') ??
    LOCALES.ru
  return LOCALE_TO_BACKEND_HEADER[toAppLocale(stored)] ?? stored
}
